import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Send, ArrowRight } from "lucide-react";
import { api, fmtErr, streamPost, CATEGORY_COLORS, timeAgo } from "../../lib/api";
import { LeaOrb } from "../../components/Lea";

export default function AskInbox() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);
  const suggestions = t("ask.suggestions");

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  const ask = async (q) => {
    const question = (q || input).trim();
    if (!question || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: question }, { role: "lea", text: "", cited: [], streaming: true }]);
    setBusy(true);
    try {
      const done = await streamPost("/ask/stream", { question }, (delta) => {
        setMessages((m) => {
          const copy = [...m];
          const last = copy[copy.length - 1];
          copy[copy.length - 1] = { ...last, text: last.text + delta };
          return copy;
        });
      }, crypto.randomUUID());
      let cited = [];
      if (done?.sources?.length) {
        const { data } = await api.post("/emails/by-ids", { ids: done.sources });
        cited = data.emails;
      }
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { ...copy[copy.length - 1], cited, streaming: false };
        return copy;
      });
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { ...copy[copy.length - 1], text: fmtErr(e, "Je n'ai pas pu analyser vos emails. Réessayez."), streaming: false };
        return copy;
      });
    }
    setBusy(false);
  };

  return (
    <div className="max-w-3xl mx-auto flex flex-col h-[calc(100vh-9rem)]" data-testid="ask-inbox-page">
      <div className="flex items-center gap-3 mb-6">
        <LeaOrb size={38} />
        <div>
          <h1 className="font-display font-black text-2xl tracking-tighter">{t("ask.title")}</h1>
          <p className="text-muted-foreground text-xs">{t("ask.subtitle")}</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto space-y-5 pb-4 pr-1">
        {messages.length === 0 && (
          <div className="py-8">
            <p className="text-sm text-muted-foreground mb-5">{t("ask.tryExamples")}</p>
            <div className="flex flex-wrap gap-2">
              {suggestions.map((s) => (
                <button key={s} onClick={() => ask(s)} data-testid={`suggestion-${s.slice(0, 18).replace(/[^a-z]/gi, "-")}`}
                  className="rounded-full border border-border bg-card px-4 py-2 text-xs font-medium hover:border-electric/50 hover:text-electric transition-colors">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className={m.role === "user" ? "flex justify-end" : "flex gap-3"}>
            {m.role === "lea" && <LeaOrb size={26} className="mt-1" />}
            <div className={`max-w-[85%] rounded-2xl px-5 py-3.5 text-sm leading-relaxed ${
              m.role === "user" ? "bg-electric text-white rounded-br-sm" : "bg-card border border-border rounded-tl-sm"}`}
              data-testid={`ask-message-${i}`}>
              <p className="whitespace-pre-wrap">{m.text}</p>
              {m.cited?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-border/60 space-y-1.5">
                  <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{t("ask.cited")}</p>
                  {m.cited.map((e) => (
                    <button key={e.email_id} onClick={() => navigate(`/app/inbox/${e.email_id}`)}
                      data-testid={`cited-email-${e.email_id}`}
                      className="w-full flex items-center gap-2 rounded-lg bg-secondary/60 hover:bg-electric/10 px-3 py-2 text-left transition-colors group">
                      <span className={`text-[9px] font-semibold rounded-full px-2 py-0.5 shrink-0 ${CATEGORY_COLORS[e.category] || CATEGORY_COLORS.Autre}`}>{e.category}</span>
                      <span className="text-xs font-medium truncate flex-1">{e.subject}</span>
                      <span className="text-[10px] text-muted-foreground shrink-0">{timeAgo(e.date)}</span>
                      <ArrowRight size={11} className="text-muted-foreground group-hover:text-electric shrink-0" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        ))}
        {busy && messages[messages.length - 1]?.streaming !== true && (
          <div className="flex gap-3">
            <LeaOrb size={26} className="mt-1" />
            <div className="rounded-2xl rounded-tl-sm border border-border bg-card px-5 py-4 space-y-2 w-72">
              <p className="text-[11px] text-muted-foreground animate-pulse-soft">{t("ask.analyzing")}</p>
              <div className="skeleton-shimmer h-2.5 rounded w-full" />
              <div className="skeleton-shimmer h-2.5 rounded w-4/6" />
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="relative mt-2">
        <input value={input} onChange={(e) => setInput(e.target.value)}
          placeholder={t("ask.placeholder")} data-testid="ask-input"
          className="w-full rounded-full border border-border bg-card pl-6 pr-14 py-4 text-sm outline-none focus:border-electric focus:ring-2 focus:ring-electric/20 transition-shadow shadow-[0_8px_32px_rgba(0,0,0,0.04)]" />
        <button type="submit" disabled={busy || !input.trim()} data-testid="ask-submit"
          className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-electric text-white flex items-center justify-center hover:bg-blue-700 transition-colors disabled:opacity-40">
          <Send size={15} />
        </button>
      </form>
    </div>
  );
}
